
<template>
    <div class="draft-area" :class="{
        'elimination-draft': isEliminationDraft,
        'two-player-game': numOfPlayers === 2,
    }">
        <div class="draft-area-header">
            <div class="header-section section-left">
                <router-link to="/rooms" class="back-link">
                    <button class="btn btn-textual back-btn">
                        <fa-icon icon="arrow-left" />
                        <span class="back-btn-text">Back</span>
                    </button>
                </router-link>
            </div>
            <div class="header-section section-middle">
                <div class="players-container" v-if="draftingIsActive || isSpectator">
                    <div class="player-wrapper"
                        v-for="playerId in turnOrder"
                        :key="`player-${playerId}`"
                    >
                        <player-label
                            :player="findPlayer(playerId)"
                            :class="{['drafting']: isDraftingPlayer(playerId)}"
                            :linkToProfile="true"
                            :theme="gameState.theme"
                        />
                        <div class="drafting-indicator" v-if="isDraftingPlayer(playerId)">
                            {{ eliminationDrafting ? 'Eliminating' : 'Drafting' }}
                            <fa-icon class="drafting-arrow-icon" icon="caret-down" />
                        </div>
                    </div>
                </div>
                <div
                    class="manual-setup"
                    :class="{'setup-complete': !choosePlayOrderActive || isSpectator}"
                    v-if="choosePlayOrderEnabled"
                >
                    <div class="manual-setup-section"
                        :class="{
                            'inactive': !isActivePlayer(player.id) ||
                            (isActivePlayer(player.id) && gameStatus !== 'selecting_draft_order')
                        }"
                        v-if="!isSpectator && choosePlayOrderActive && gameStatus === 'selecting_draft_order'"
                    >
                        <div class="manual-setup-title">
                            Who should <strong>draft</strong> first?
                        </div>
                        <div class="manual-setup-options">
                            <div class="player-wrapper"
                                v-for="player in players"
                                :key="`starting-player-${player.id}`"
                                :class="{
                                    'selected': isDraftingPlayer(player.id) &&
                                    gameStatus !== 'selecting_draft_order'
                                }"
                                @click="onSelectDraftingPlayer(player.id)"
                            >
                                <player-label :player="player" :theme="gameState.theme"/>
                            </div>
                        </div>
                    </div>
                    <div class="manual-setup-section"
                        v-if="gameStatus !== 'selecting_draft_order'"
                    >
                        <div>
                            <span class="manual-setup-selection">{{selectedDraftingPlayer.user.username}}</span> will
                            <span class="manual-setup-selection">draft</span> first
                        </div>
                    </div>
                    <div class="manual-setup-section"
                        :class="{
                            'inactive': !isActivePlayer(player.id) ||
                            (isActivePlayer(player.id) && gameStatus !== 'selecting_play_order')
                        }"
                        v-if="!isSpectator && choosePlayOrderActive"
                    >
                        <div class="manual-setup-title">
                            Who should <strong>play</strong> first?
                        </div>
                        <div class="manual-setup-options">
                            <div class="player-wrapper"
                                v-for="player in players"
                                :key="`starting-player-${player.id}`"
                                :class="{
                                    'selected': isActivePlayer(player.id) &&
                                    !choosePlayOrderActive
                                }"
                                @click="onSelectStartingPlayer(player.id)"
                            >
                                <player-label :player="player" :theme="gameState.theme"/>
                            </div>
                        </div>
                    </div>
                    <div class="manual-setup-section"
                        v-if="!choosePlayOrderActive"
                    >
                        <div>
                            <span class="manual-setup-selection">{{selectedStartingPlayer.user.username}}</span> will
                            <span class="manual-setup-selection">play</span> first
                        </div>
                    </div>
                </div>
            </div>
            <div class="header-section section-right">
                <sound v-if="!isSpectator" />
            </div>
        </div>
        <div
            class="expansions-bar"
            :class="{
                'siege-expansion': forts.length,
                'nobility-expansion': decrees.length,
            }"
        >
            <div class="forts-wrapper"
                v-if="forts && forts.length"
            >
                <div class="fort-layout-label">
                    Fortifications:
                </div>
                <div class="fort-layout-card-wrapper">
                    <fort-layout :forts="forts" />
                </div>
            </div>
            <decrees-bar
                :decrees="decrees"
                :renderLabel="true"
                :drafting="true"
                :hideMarkers="hideDecreeMarkers"
                v-if="decrees.length"
            />
        </div>
        <div class="units-container" v-if="!isMobile">
            <div class="unit-card-wrapper" v-for="(draftUnit, i) in draftUnits" :key="`draft-unit-${i}`">
                <unit-card
                    :unit-type="draftUnit.unitType.type"
                    :image-name="draftUnit.unitType.imageName"
                    :quantity="draftUnit.unitType.quantity"
                    :class="{
                        ['drafted']: draftingPlayer(draftUnit),
                        ['eliminated']: isEliminated(draftUnit.id) || isHoveredOver(draftUnit.id),
                    }"
                    :eliminated="isEliminated(draftUnit.id) || isHoveredOver(draftUnit.id)"
                />
                <button
					v-if="!eliminationDrafting &&
					!draftingPlayer(draftUnit) &&
					!choosePlayOrderActive"
                    class="btn btn-outline select-unit-btn"
                    @click="onDraftUnit(draftUnit.unitTypeId)"
                    :class="{
                        ['btn-3d']: !draftingPlayer(draftUnit),
                        ['btn-disabled']: draftingPlayer(draftUnit) || isEliminated(draftUnit.id)
                    }"
                    :disabled="draftingPlayer(draftUnit) || isEliminated(draftUnit.id)"
                >
                    Select
                </button>
                <button
                    v-if="eliminationDrafting &&
						!draftingPlayer(draftUnit) &&
						!choosePlayOrderActive &&
						!championsUnitTypes.includes(draftUnit.unitType.type)"
                    class="btn btn-outline select-unit-btn"
                    @click="onEliminateDraftUnit(draftUnit.unitTypeId)"
                    @mouseover="onEliminateHover(draftUnit.id)"
                    @mouseleave="onEliminateLeave()"
                    :class="{
                        ['btn-3d']: !draftingPlayer(draftUnit),
                        ['btn-disabled']: isEliminated(draftUnit.id)
                    }"
                    :disabled="isEliminated(draftUnit.id)"
                >
                    <fa-icon icon="ban" class="icon" />
                    <span class="btn-text">Eliminate</span>
                </button>
                <player-label :player="draftingPlayer(draftUnit)" :theme="gameState.theme" v-if="draftingPlayer(draftUnit)"/>
            </div>
        </div>
        <div class="units-container-mobile" v-if="isMobile">
            <div class="unit-coin-row" v-for="(row, i) in unitCoinRows" :key="`unit-coin-row-${i}`">
                <div class="unit-coin-wrapper" v-for="(draftUnit, i) in row" :key="`draft-unit-${i}`">
                    <div class="unit-image-wrapper"
                        :class="{
                            eliminated: isEliminated(draftUnit.id),
                            drafted: draftingPlayer(draftUnit),
                        }"
                    >
                        <img
                            class="unit-image"
                            :src="getAssetUrl(`@/assets/units/${draftUnit.unitType.imageName}`)"
                            draggable="false"
                        />
                        <fa-icon icon="skull" class="icon"  v-if="isEliminated(draftUnit.id)" />
                    </div>
                    <span
                        class="icon-wrapper eliminate"
                        @click="onEliminateDraftUnit(draftUnit.unitTypeId)"
                        @mouseover="onEliminateHover(draftUnit.id)"
                        @mouseleave="onEliminateLeave()"
                        :class="{
                            ['disabled']: isEliminated(draftUnit.id)
                        }"
                        v-if="eliminationDrafting && !draftingPlayer(draftUnit) && !choosePlayOrderActive"
                    >
                        <fa-icon icon="skull" class="icon" />
                    </span>
                    <span
                        class="icon-wrapper select"
                        @click="onDraftUnit(draftUnit.unitTypeId)"
                        :class="{
                            ['disabled']: draftingPlayer(draftUnit) || isEliminated(draftUnit.id)
                        }"
                        v-if="!eliminationDrafting && !draftingPlayer(draftUnit) && !choosePlayOrderActive"
                    >
                        <fa-icon icon="check" class="icon" />
                    </span>
                    <player-label :player="draftingPlayer(draftUnit)" :theme="gameState.theme" v-if="draftingPlayer(draftUnit)"/>
                </div>
            </div>
        </div>
        <div class="draft-area-footer">
            <button class="btn btn-muted btn-3d btn-leave"
                v-if="player"
                @click="onLeaveGame(gameId)"
            >
                Leave & Cancel Game
            </button>
        </div>
        <div class="view-cards-wrapper" v-if="isMobile" @click="() => setShowDraftUnitsModal(true)">
            <icon class="icon" icon="cards"/>
        </div>
        <unit-cards-modal
            v-if="isMobile && showDraftUnitsModal"
            type="draft-units-modal"
            :show="showDraftUnitsModal"
            :on-close="() => setShowDraftUnitsModal(false)"
        >
            <unit-card
                v-for="unit in draftUnits"
                :key="`unit-card-${unit.unitType.type}`"
                :unit-type="unit.unitType.type"
                :image-name="unit.unitType.imageName"
                :quantity="unit.unitType.quantity"
            />
        </unit-cards-modal>
    </div>
</template>

<script>
import find from 'lodash.find';
import { getAssetUrl } from '../../helpers/assets';
import {
    IDraftType,
    IGameStatus,
} from '../Game/Game-types';
import {
	championsUnitTypes
} from '../Unit/Unit-types';

export default {
    props: ['isMobile'],
    name: 'DraftArea',
    created() {
        this.selectedDraftingPlayerId = null;
    },
    beforeUnmount() {
        this.selectedDraftingPlayerId = null;
    },
    data() {
        return {
            hoveredDraftUnitId: null,
            selectedDraftingPlayerId: null,
            showDraftUnitsModal: false,
			championsUnitTypes,
        };
    },
    watch: {
        gameStatus(current, prev) {
            if (prev === IGameStatus.SELECTING_DRAFT_ORDER &&
                current === IGameStatus.SELECTING_PLAY_ORDER
            ) {
                this.selectedDraftingPlayerId = this.draftingPlayerId;
            }
        },
    },
    computed: {
        activePlayerId() {
            return this.gameState.activePlayerId;
        },
        choosePlayOrderActive() {
            return this.gameStatus === IGameStatus.SELECTING_DRAFT_ORDER ||
                this.gameStatus === IGameStatus.SELECTING_PLAY_ORDER;
        },
        choosePlayOrderEnabled() {
            return this.gameState.choosePlayOrder;
        },
        decrees() {
            return this.gameState.decrees;
        },
        draftingIsActive() {
            return this.gameStatus === IGameStatus.DRAFTING ||
                this.gameStatus === IGameStatus.ELIMINATION_DRAFTING;
        },
        draftingPlayerId() {
            return this.gameState.draftingPlayerId;
        },
        draftUnits() {
            return this.$store.state.draftState.draftUnits;
        },
        eliminationDrafting() {
            return this.gameStatus === 'elimination_drafting';
        },
        eliminatedUnitIds() {
            const eliminatedUnitIds = [];

            this.draftUnits.map((unitType) => {
                if (unitType.eliminated) {
                    eliminatedUnitIds.push(unitType.id);
                }
            });

            return eliminatedUnitIds;
        },
		hasUndraftedChampions() {
			const champions = this.draftUnits.filter(unit => championsUnitTypes.includes(unit.unitType.type));
			return champions.length && champions.some(champion => !champion.playerId);

		},
        forts() {
            return this.gameState.forts;
        },
        hideDecreeMarkers() {
            return this.forts && this.forts.length;
        },
        isEliminationDraft() {
            return this.gameState.draftType === IDraftType.ELIMINATION_DRAFT;
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        gameState() {
            return this.$store.state.gameState;
        },
        gameStatus() {
            return this.gameState.state;
        },
        numOfPlayers() {
            return this.gameState.numOfPlayers;
        },
        player() {
            return this.gameState.players.filter(player => player.user.id === this.userId)[0];
        },
        players() {
            return this.gameState.players;
        },
        selectedDraftingPlayer() {
            if (this.selectedDraftingPlayerId) {
                const selectedDraftingPlayer = this.players.filter(player => player.id === this.selectedDraftingPlayerId)[0];

                if (selectedDraftingPlayer) {
                    return selectedDraftingPlayer;
                }
            }

            const firstDraftedUnit = find(this.draftUnits, { order: 1 });
            const playerId = firstDraftedUnit ? firstDraftedUnit.playerId : this.draftingPlayerId;

            const eliminatedUnits = this.draftUnits.filter(draftUnit => draftUnit.eliminated);

            if (this.gameStatus === IGameStatus.ELIMINATION_DRAFTING &&
                [1, 3].includes(eliminatedUnits.length)
            ) {
                return this.players.filter(player => player.id !== playerId)[0];
            }

            return this.players.filter(player => player.id === playerId)[0];
        },
        selectedStartingPlayer() {
            return this.players.filter(player => player.id === this.activePlayerId)[0];
        },
        turnOrder() {
            return this.gameState.turnOrder;
        },
        unitCoinRows() {
            const units = this.draftUnits;
            const rows = [];
            let currentRow = [];

            for (const [index, unit] of units.entries()) {
                currentRow.push(unit);

                if (currentRow.length === 4 || index === units.length - 1) {
                    rows.push(currentRow);
                    currentRow = [];
                }
            }

            return rows;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
    },
    methods: {
        getAssetUrl,
        draftingPlayer(draftUnit) {
            return draftUnit.playerId && this.players.filter(player => player.id === draftUnit.playerId)[0];
        },
        findPlayer(playerId) {
            return this.players.filter(player => player.id === playerId)[0];
        },
        isActivePlayer(playerId) {
            return this.activePlayerId === playerId;
        },
        isDraftingPlayer(playerId) {
            return this.draftingPlayerId === playerId;
        },
        isEliminated(draftUnitId) {
            return this.eliminatedUnitIds.includes(draftUnitId);
        },
        isHoveredOver(draftUnitId) {
            return this.eliminationDrafting && draftUnitId === this.hoveredDraftUnitId;
        },
        onDraftUnit(unitTypeId) {
            this.$store.dispatch('setSoundReady');
            return this.$store.dispatch('draftUnit', { gameId: this.gameId, unitTypeId });
        },
        onEliminateDraftUnit(unitTypeId) {
            this.$store.dispatch('setSoundReady');
            this.$store.dispatch('eliminateDraftUnit', { gameId: this.gameId, unitTypeId });
        },
        onEliminateHover(draftUnitId) {
            this.hoveredDraftUnitId = draftUnitId;
        },
        onEliminateLeave() {
            this.hoveredDraftUnitId = null;
        },
        onLeaveGame(gameId) {
            this.$store.dispatch('openModal', { type: 'leaveGame', data: { gameId } });
        },
        setShowDraftUnitsModal(value) {
            this.showDraftUnitsModal = value;
        },
        async onSelectDraftingPlayer(playerId) {
            if (!this.isActivePlayer(this.player.id)) {
                return;
            }

            const payload = {
                gameId: this.gameId,
                draftingPlayerId: playerId,
            };
            await this.$store.dispatch('assignPlayer', payload);
        },
        async onSelectStartingPlayer(playerId) {
            if (!this.isActivePlayer(this.player.id)) {
                return;
            }

            const payload = {
                gameId: this.gameId,
                startingPlayerId: playerId,
            };
            await this.$store.dispatch('assignPlayer', payload);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

	.game-container {
		.draft-area {
			margin: 0 auto;
			max-width: 900px;
			position: relative;

			.draft-title {
				font-family: 'RobotoSlab', 'Arial';
				font-size: 1.2em;
				padding: 17px 21px 12px;
				text-align: center;
				vertical-align: middle;
				z-index: 5;
				white-space: nowrap;
				border-bottom: 2px solid $coin-border;
				display: inline-block;
				margin-bottom: 20px;
				color: $dark-blue;

				&.black {
					color: $dark-gray;
					border-left: 5px solid $dark-gray;
				}

				.turn-icon-wrapper {
					display: inline-block;
					vertical-align: middle;
					margin-right: 5px;

					.turn-icon {
						height: 30px;
						width: 30px;
						vertical-align: middle;
					}
				}

				.text {
					display: inline-block;
					vertical-align: middle;
					text-transform: uppercase;
				}
			}

			.units-container {
				display: flex;
				flex-wrap: wrap;
				justify-content: center;

				.unit-card-wrapper {
					margin: 0 10px 15px 10px;

					.unit-card {
						margin-bottom: 10px;

						&.drafted {
							opacity: 0.5;
						}
					}

					.player-label {
						background: none;
						border: none;
						font-size: .8em;
						line-height: 1.6em;
						padding-top: 6px;
						padding-bottom: 6px;

						&.black {
							color: $dark-gray;
						}
					}

					.select-unit-btn {
						padding: 7px 30px;
						font-size: .8em;
						border: 1px solid $light-gray-alt;
						display: inline-flex;
						justify-content: center;
						align-items: center;
						gap: 5px;

						&.btn-3d {
							box-shadow: 0 3px $light-gray-alt;

							&:hover {
								top: 1px;
								box-shadow: 0 1px $light-gray-alt;
							}
						}

						.btn-text,
						.icon {
							vertical-align: middle;
						}
					}
				}
			}

			.units-container-mobile {
				.unit-coin-row {
					display: flex;
					justify-content: center;
					margin-bottom: 10px;

					&:last-of-type {
						margin-bottom: 0;
					}

					.unit-coin-wrapper {
						margin: 0 1%;
						display: flex;
						flex-direction: column;
						width: 22%;

						&:first-of-type,
						&:last-of-type {
							margin-left: 0;
						}

						> * {
							align-self: center;
						}

						.unit-image-wrapper {
							position: relative;

							&.eliminated {
								&:before {
									background: $dark-gray;
									border-radius: 50%;
									content: '';
									height: 100%;
									left: 0;
									opacity: .65;
									position: absolute;
									top: 0;
									width: 100%;
								}

								.icon {
									border-radius: 50%;
									align-self: center;
									color: $extra-light-gray;
									height: auto;
									left: 50%;
									opacity: .75;
									position: absolute;
									top: 50%;
									transform: translate(-50%, -50%);
									width: 45%;
								}

								.unit-image {
									opacity: .5;
								}
							}

							&.drafted {
								.unit-image {
									opacity: .5;
								}
							}

							.unit-image {
								height: auto;
								width: auto;
								max-height: 100%;
								max-width: 100%;
								display: block;
							}
						}

						.icon-wrapper {
							margin-top: 10px;

							&.select {
								.icon {
									padding: 6px 7.1px;
								}
							}

							&.disabled {
								.icon {
									background: $extra-light-gray;
									cursor: not-allowed;
									opacity: .5;
								}
							}

							.icon {
								background: white;
								border: 1px solid $sage-brown;
								border-radius: 50%;
								color: $sage-brown;
								cursor: pointer;
								font-size: 1.1em;
								padding: 6px;
							}
						}
					}
				}
			}

			.draft-area-header {
				display: flex;
				margin: 20px auto;

				.section-middle {
					width: 100%;
				}

				.section-left,
				.section-right {
					min-width: 20px;
					padding: 2%;

					.back-btn {
						.back-btn-text {
							display: none;
						}
					}

					.sound-control-wrapper {
						min-height: 52px;
					}
				}

				.players-container {
					display: flex;
					justify-content: space-evenly;
					flex: 1 1 auto;

					.player-wrapper {
						position: relative;
						margin: 0 8.5px;

						.player-label {
							background: none;
							padding: 8% 12%;
							border-width: 0;
							border-radius: 0;
							border-style: solid;
							border-top-width: 4px;
							border-bottom-width: 4px;
							border-color: transparent;

							&.black {
								color: $dark-gray;

								&.drafting {
									border-color: $dark-gray;
								}
							}

							&.drafting {
								border-color: $burnished-gold;
							}
						}

						.drafting-indicator {
							display: none;
							font-weight: bold;
							position: absolute;
							left: 50%;
							text-transform: uppercase;
							top: -35px;
							transform: translateX(-50%);
							width: 100%;

							.drafting-arrow-icon {
								bottom: -14px;
								left: 50%;
								position: absolute;
								transform: translateX(-50%);
							}
						}
					}
				}
			}

			.draft-area-footer {
				margin: 20px auto;

				.btn-leave {
					font-weight: 300;
					font-size: .85em;
					padding: 5px 7px;
					color: $gray;
				}
			}

			.expansions-bar {
				margin-bottom: 20px;
				display: flex;
				justify-content: center;
				flex-direction: column;

				&.nobility-expansion {
					&.siege-expansion {
						.forts-wrapper {
							margin-bottom: 15px;
						}
					}
				}

				.decrees-bar {
					flex-direction: column;
					margin-bottom: 0;
				}

				.decree-widget {
					&.btn {
						&.btn-3d {
							&.btn-gray {
								&:hover {
									box-shadow: 0 3px $medium-gray;
									top: 0;
								}
							}
						}
					}
				}

				.forts-wrapper {
					display: flex;
					justify-content: center;
					align-self: center;

					.fort-layout-label {
						align-self: center;
						margin-right: 20px;
						font-weight: bold;
						font-family: 'RobotoSlab', 'Avenir', Helvetica, Arial, sans-serif;
					}

					.fort-layout-card-wrapper {
						align-self: center;
					}
				}
			}

			.btn-disabled {
				cursor: not-allowed;
			}

			.manual-setup {
				display: flex;
				justify-content: space-evenly;
				flex-wrap: wrap;
				gap: 15px;
				width: 100%;

				&.setup-complete {
					margin-top: 25px;
				}

				.manual-setup-section {
					align-self: center;
					background: white;
					border-radius: 4px;
					box-shadow: 0px 1px 2px 0px #afa7a7;
					padding: 10px;
					position: relative;

					&.inactive {
						cursor: not-allowed;
						opacity: .9;

						&:after {
							position: absolute;
							content: '';
							left: 0;
							top: 0;
							background: rgba(0,0,0,.5);
							height: 100%;
							width: 100%;
							border-radius: 4px;
						}
					}

					&:not(.inactive) {
						.manual-setup-options {
							.player-wrapper {
								&:hover {
									&:after {
										content: '';
										position: absolute;
										height: 4px;
										width: 97%;
										background: $burnished-gold;
										bottom: -9px;
										left: 50%;
										transform: translateX(-50%);
										border-radius: 4px;
									}
								}
							}
						}
					}

					.manual-setup-selection {
						font-weight: bold;
						position: relative;

						&:after {
							content: '';
							position: absolute;
							height: 2px;
							width: 100%;
							background: $burnished-gold;
							bottom: -4px;
							left: 50%;
							transform: translateX(-50%);
							border-radius: 2px;
						}
					}

					.manual-setup-title {
						margin-bottom: 15px;
					}

					.manual-setup-options {
						display: flex;

						.player-wrapper {
							cursor: pointer;
							position: relative;

							&.selected {
								&:after {
									content: '';
									position: absolute;
									height: 4px;
									width: 97%;
									background: $burnished-gold;
									bottom: -9px;
									left: 50%;
									transform: translateX(-50%);
									border-radius: 4px;
								}
							}

							&:not(:first-of-type) {
								margin-left: 15px;
							}
						}
					}
				}
			}
		}

		.view-cards-wrapper {
			background: $off-white;
			border: 1px solid rgba(119,109,109,0.35);
			border-radius: 50%;
			bottom: 15px;
			box-shadow: 0px 0px 2px 1px rgba(175,175,175,0.35);
			cursor: pointer;
			padding: 6px;
			position: fixed;
			left: 20px;
			height: 21px;
			width: 21px;
			z-index: 10;

			.icon {
				fill: $sage-brown;
				position: absolute;
				left: 50%;
				top: 50%;
				transform: translate(-50%, -50%);
				width: 70%;
				height: 70%;
			}
		}
	}

	@media only screen and (min-width: $screen-xs) {
		.game-container {
			.draft-area {
				.manual-setup {
					.manual-setup-section {
						padding: 25px 15px;
					}

					.manual-setup-title {
						margin-bottom: 25px;
					}
				}

				.draft-area-header {
					.section-left,
					.section-right {
						min-width: 80px;
						padding: 15px;

						.back-btn {
							display: flex;

							> * {
								align-self: center;
							}

							.back-btn-text {
								display: inline-block;
								margin-left: 5px;
							}
						}
					}
				}

				&.elimination-draft {
					&.two-player-game {
						max-width: 1150px;
					}
				}

				.expansions-bar {
					.decrees-bar {
						flex-direction: row;

						.decree-name {
							font-weight: 400;
						}

						.decrees-label {
							margin-bottom: 0;
							margin-right: 20px;
						}
					}

					:deep(.decrees-bar) {
						flex-direction: row;

						.decree-name {
							font-weight: 400;
						}

						.decrees-label {
							margin-bottom: 0;
							margin-right: 20px;
						}
					}

					&.siege-expansion {
						.forts-wrapper {
							margin-bottom: 20px;
							margin-right: 25px;
						}
					}
				}

				.draft-area-header {
					.players-container {
						justify-content: center;

						.player-wrapper {
							.player-label {
								padding: 11px 16px;
							}
						}
					}
				}

				.decree-widget-wrapper {
					.decree-widget {
						&.disabled {
							opacity: 1;
							cursor: default;

							.decree-marker {
								opacity: 1;
								cursor: default;
							}
						}

						.decree-markers {
							.decree-marker-wrapper {
								&.black {
									.decree-marker {
										opacity: 1;
									}
								}
							}
						}
					}
				}
			}
		}
	}

	@media only screen and (min-width: $screen-sm) {
		.game-container {
			.draft-area {

				.draft-area-header {
					margin: 50px auto 20px;

					.players-container {
						.player-wrapper {
							.drafting-indicator {
								display: block;
							}
						}
					}
				}

				.expansions-bar {
					flex-direction: row;

					.forts-wrapper {
						margin-right: 25px;
						margin-bottom: 0;
					}

					&.nobility-expansion {
						&.siege-expansion {
							.forts-wrapper {
								margin-bottom: 0;
							}
						}
					}
				}
			}
		}
	}
</style>
